import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ListChecks, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

// Admin sub-module: rename / add / remove options for every configurable multi-choice
// field in the app (asset type, insurance holder/coverage type, defect type/severity...).
export default function PickListsSection() {
  const qc = useQueryClient();
  const { data: groups = [] } = useQuery({ queryKey: ["pick-lists"], queryFn: () => api.get("/pick-lists").then((r) => r.data) });
  const [activeKey, setActiveKey] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!activeKey && groups.length) setActiveKey(groups[0].field_key);
  }, [groups, activeKey]);

  const active = groups.find((g) => g.field_key === activeKey);

  useEffect(() => {
    if (active) setRows(active.options.map((o) => ({ ...o })));
  }, [activeKey, active?.customized]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (options) => api.put(`/pick-lists/${activeKey}`, { options }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pick-lists"] }); toast.success("Liste enregistrée"); },
    onError: (e) => toast.error(extractError(e, "Échec de l'enregistrement")),
  });
  const reset = useMutation({
    mutationFn: () => api.post(`/pick-lists/${activeKey}/reset`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pick-lists"] }); toast.success("Liste réinitialisée aux valeurs par défaut"); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });

  const updateRow = (i, field, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: val } : row)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addRow = () => setRows((r) => [...r, { value: "", label: "" }]);
  const move = (i, dir) => setRows((r) => {
    const j = i + dir;
    if (j < 0 || j >= r.length) return r;
    const copy = [...r];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const handleSave = () => {
    const cleaned = rows.map((r) => ({ value: r.value.trim(), label: r.label.trim() }));
    if (cleaned.some((r) => !r.value || !r.label)) {
      toast.error("Chaque option doit avoir une valeur technique et un libellé");
      return;
    }
    const values = cleaned.map((r) => r.value);
    if (new Set(values).size !== values.length) {
      toast.error("Valeurs techniques dupliquées");
      return;
    }
    save.mutate(cleaned);
  };

  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-4">
      <Card className="p-2 border-slate-200 h-fit">
        {groups.map((g) => (
          <button
            key={g.field_key}
            data-testid={`picklist-group-${g.field_key}`}
            onClick={() => setActiveKey(g.field_key)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition ${activeKey === g.field_key ? "bg-[#023E73] text-white font-semibold" : "hover:bg-slate-100 text-slate-700"}`}
          >
            {g.label}
            {g.customized && <span className={`ml-1.5 text-[10px] ${activeKey === g.field_key ? "text-blue-200" : "text-[#023E73]"}`}>• personnalisé</span>}
          </button>
        ))}
      </Card>

      <Card className="p-5 border-slate-200">
        {!active ? (
          <div className="text-sm text-slate-400">Sélectionne un champ à configurer.</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display font-semibold text-lg flex items-center gap-2"><ListChecks size={16} className="text-[#023E73]" />{active.label}</div>
                <p className="text-xs text-slate-500 mt-0.5">Renomme, ajoute ou supprime les options de ce champ à choix multiple.</p>
              </div>
              <Button data-testid="picklist-reset" variant="outline" size="sm" onClick={() => reset.mutate()} disabled={reset.isPending}>
                <RotateCcw size={13} className="mr-1.5" /> Valeurs par défaut
              </Button>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-[11px] uppercase tracking-wider font-semibold text-slate-400 px-1">
                <span>Valeur technique</span><span>Libellé affiché</span><span></span>
              </div>
              {rows.map((r, i) => (
                <div key={i} data-testid={`picklist-row-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <Input data-testid={`picklist-value-${i}`} value={r.value} onChange={(e) => updateRow(i, "value", e.target.value)} placeholder="ex: logistics" className="font-mono text-xs" />
                  <Input data-testid={`picklist-label-${i}`} value={r.label} onChange={(e) => updateRow(i, "label", e.target.value)} placeholder="ex: Logistique" />
                  <div className="flex gap-0.5">
                    <button data-testid={`picklist-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30"><ArrowUp size={13} /></button>
                    <button data-testid={`picklist-down-${i}`} onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30"><ArrowDown size={13} /></button>
                    <button data-testid={`picklist-remove-${i}`} onClick={() => removeRow(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4">
              <Button data-testid="picklist-add-option" variant="outline" size="sm" onClick={addRow}><Plus size={13} className="mr-1.5" /> Ajouter une option</Button>
              <Button data-testid="picklist-save" size="sm" className="text-white" style={{ background: "#023E73" }} onClick={handleSave} disabled={save.isPending}>
                <Save size={13} className="mr-1.5" /> {save.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-3">
              Supprimer une option n'affecte pas les données déjà enregistrées avec cette valeur — elles continueront de s'afficher telles quelles.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
